import { FC, useEffect, useMemo, useState } from 'react';
import { Card, Text, LineChart } from '@tremor/react';
import * as Sentry from '@sentry/react';
import SummaryCard from '../../../../components/shared/Charts/SummaryCard/SummaryCard';
import { ServiceSignupFunnel, SignupFunnelData, FunnelPeriod } from '../../../../services/service-signup-funnel';
import { UNIT } from '../../helper';

const PERIOD_OPTIONS: { label: string; value: FunnelPeriod }[] = [
  { label: 'Vecka', value: 'week' },
  { label: 'Månad', value: 'month' },
  { label: 'År', value: 'year' },
];

const SUBSCRIPTION_STEPS = new Set(['subscription_prio_activated', 'subscription_marketplace_activated']);

const STEP_DESCRIPTIONS: Record<string, string> = {
  signup_wizard_started: 'Antal unika sessioner som öppnat /supplier-sign-up. Mäts med en unik sessionId per wizard-start.',
  signup_wizard_step_company: 'Unika sessioner som slutfört steg 1 (företagsuppgifter).',
  signup_wizard_step_categories: 'Unika sessioner som valt minst en yrkeskategori.',
  signup_wizard_step_places: 'Unika sessioner som valt minst ett verksamhetsområde.',
  signup_wizard_step_about_you: 'Unika sessioner som fyllt i sina personuppgifter.',
  signup_wizard_step_invite: 'Unika sessioner som passerat kollega-steget (inbjudna eller hoppat över).',
  signup_wizard_completed: 'Unika konton som verifierats och skapats i databasen.',
  subscription_activated: 'Totalt antal nya Prio- + Marknadsplatsen-prenumerationer aktiverade. Räknar bara nya ordrar (inte uncancel eller renewals).',
};

interface FunnelRow {
  step: string;
  label: string;
  value: number;
  count: number;
  uniqueSessions: number;
  isSubscription: boolean;
}

interface RowProps {
  row: FunnelRow;
  prevValue: number | null;
  baselineValue: number;
}

/** Single funnel row — label, bar scaled to baseline with count inside, conversion pill. */
const FunnelBarRow: FC<RowProps> = ({ row, prevValue, baselineValue }) => {
  const barWidth = baselineValue > 0 ? (row.value / baselineValue) * 100 : 0;
  const conversionPct = prevValue && prevValue > 0
    ? Math.round((row.value / prevValue) * 1000) / 10
    : null;
  const clampedBarWidth = Math.max(barWidth, 0.5);

  return (
    <div className="flex items-center gap-3" style={{ padding: '4px 0' }}>
      <div
        className="text-sm flex items-center"
        style={{ width: '140px', flexShrink: 0, color: 'var(--gray-700)', fontWeight: 500 }}
      >
        <span title={STEP_DESCRIPTIONS[row.step] || ''} style={{ cursor: STEP_DESCRIPTIONS[row.step] ? 'help' : 'default' }}>
          {row.label}
        </span>
      </div>
      <div
        className="flex-1 relative rounded-md overflow-hidden"
        style={{ height: '32px', background: 'var(--gray-50)' }}
      >
        {row.value > 0 && (
          <div
            style={{
              width: `${clampedBarWidth}%`,
              height: '100%',
              background: 'var(--orange-500)',
              transition: 'width 400ms ease-out',
            }}
          />
        )}
        {/* Dark number — visible where bar doesn't reach */}
        <div
          className="absolute inset-0 flex items-center justify-end pr-3 text-sm font-semibold"
          style={{
            color: 'var(--gray-900)',
            fontVariantNumeric: 'tabular-nums',
            clipPath: `inset(0 0 0 ${clampedBarWidth}%)`,
          }}
        >
          {row.value.toLocaleString('sv-SE')}
        </div>
        {/* White number — visible where bar reaches */}
        <div
          className="absolute inset-0 flex items-center justify-end pr-3 text-sm font-semibold"
          style={{
            color: '#fff',
            fontVariantNumeric: 'tabular-nums',
            clipPath: `inset(0 ${100 - clampedBarWidth}% 0 0)`,
          }}
        >
          {row.value.toLocaleString('sv-SE')}
        </div>
      </div>
      <div
        className="text-xs flex-shrink-0 text-center"
        style={{
          width: '64px',
          fontVariantNumeric: 'tabular-nums',
          color: conversionPct === null ? 'transparent' : (conversionPct < 50 ? 'var(--red-700)' : 'var(--gray-600)'),
          fontWeight: 500,
        }}
      >
        {conversionPct !== null ? `${conversionPct}%` : ''}
      </div>
    </div>
  );
};

const SignupFunnelSection: FC = () => {
  const [data, setData] = useState<SignupFunnelData | null>(null);
  const [period, setPeriod] = useState<FunnelPeriod>('month');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ServiceSignupFunnel.getFunnelData(period)
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => {
        console.error('Failed to load signup funnel data', err);
        Sentry.captureException(err);
        if (!cancelled) setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const { rows, started, completed, prio, marketplace, conversionRate } = useMemo(() => {
    if (!data || !Array.isArray(data.funnel)) {
      return { rows: [] as FunnelRow[], started: null, completed: null, prio: null, marketplace: null, conversionRate: 0 };
    }
    const wizardRows: FunnelRow[] = data.funnel
      .filter(s => !SUBSCRIPTION_STEPS.has(s.step))
      .map(s => ({ ...s, value: s.uniqueSessions, isSubscription: false }));

    const prioSrc = data.funnel.find(s => s.step === 'subscription_prio_activated');
    const marketplaceSrc = data.funnel.find(s => s.step === 'subscription_marketplace_activated');

    // Combined activation step: Prio + Marknadsplatsen (parallel outcomes, not sequential)
    const activatedRow: FunnelRow = {
      step: 'subscription_activated',
      label: 'Aktiverat abonnemang',
      value: (prioSrc?.count || 0) + (marketplaceSrc?.count || 0),
      count: (prioSrc?.count || 0) + (marketplaceSrc?.count || 0),
      uniqueSessions: 0,
      isSubscription: true,
    };

    const mapped = [...wizardRows, activatedRow];
    const startedRow = mapped.find(r => r.step === 'signup_wizard_started') || null;
    const completedRow = mapped.find(r => r.step === 'signup_wizard_completed') || null;
    const prioRow = prioSrc ? { ...prioSrc, value: prioSrc.count, isSubscription: true } : null;
    const marketplaceRow = marketplaceSrc ? { ...marketplaceSrc, value: marketplaceSrc.count, isSubscription: true } : null;
    const convRate = startedRow && startedRow.value > 0
      ? Math.round(((completedRow?.value || 0) / startedRow.value) * 1000) / 10
      : 0;
    return {
      rows: mapped,
      started: startedRow,
      completed: completedRow,
      prio: prioRow,
      marketplace: marketplaceRow,
      conversionRate: convRate,
    };
  }, [data]);

  if (loading || !data || !Array.isArray(data.funnel) || !Array.isArray(data.trend)) return null;

  const baseline = rows[0]?.value || 0;

  const trendChartData = data.trend.map(t => ({
    period: t.period,
    'Startade': t.started,
    'Slutförda': t.completed,
  }));

  return (
    <div className="flex flex-col gap-4 mt-6 mb-6">
      <div className="flex items-center justify-between">
        <Text className="font-semibold text-lg">Leverantör signup-funnel</Text>
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPeriod(opt.value)}
              className="px-3 py-1 text-sm rounded-md"
              style={{
                background: period === opt.value ? 'var(--orange-500)' : 'var(--gray-100)',
                color: period === opt.value ? '#fff' : 'var(--gray-600)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <SummaryCard
          title="Signups"
          value={completed?.value || 0}
          infoText="Unika konton skapade via signup-wizarden under perioden. Räknas när email-verifieringen lyckas och Organization + User sparats i databasen."
        />
        <SummaryCard
          title="Konvertering"
          value={conversionRate}
          unit={UNIT.PERCENTAGE}
          infoText="Andelen unika sessioner som går från Landningssida till Konto skapat. Formel: slutförda sessioner ÷ startade sessioner × 100."
        />
        <SummaryCard
          title="Prio"
          value={prio?.value || 0}
          infoText="Nya Accurator Prio-prenumerationer (product = RECOMMENDED) aktiverade under perioden. Räknar endast nya ordrar, inte uncancel eller förnyelser."
        />
        <SummaryCard
          title="Marknadsplatsen"
          value={marketplace?.value || 0}
          infoText="Nya Marknadsplatsen-prenumerationer (product = OPEN_QUOTATIONS) aktiverade under perioden. Räknar endast nya ordrar, inte uncancel eller förnyelser."
        />
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <Text className="font-semibold">Funnel</Text>
          <div className="flex gap-4 text-xs" style={{ color: 'var(--gray-500)' }}>
            <span>Antal</span>
            <span>% från föregående</span>
          </div>
        </div>

        <div className="flex flex-col">
          {rows.map((row, i) => (
            <FunnelBarRow
              key={row.step}
              row={row}
              prevValue={i > 0 ? rows[i - 1].value : null}
              baselineValue={baseline}
            />
          ))}
        </div>
      </Card>

      <Card>
        <Text className="font-semibold mb-2">Trend</Text>
        <LineChart
          data={trendChartData}
          index="period"
          categories={['Startade', 'Slutförda']}
          colors={['gray', 'orange']}
          showAnimation
        />
      </Card>
    </div>
  );
};

export default SignupFunnelSection;
